import { Input } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { InputProps } from 'antd';
import type { FC } from 'react';

type Value = number | null;

interface ClampedIntegerInputProps
  extends Omit<InputProps, 'value' | 'onChange'> {
  value: Value;
  onValueChange: (value: Value) => void;
  min?: number;
  max?: number;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const ClampedIntegerInput: FC<ClampedIntegerInputProps> = ({
  value,
  onValueChange,
  min,
  max,
  onFocus,
  onBlur,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>('');

  const isNegativeAllowed = useMemo(() => {
    const minAllows = isFiniteNumber(min) && min < 0;
    const maxAllows = isFiniteNumber(max) && max < 0;
    return minAllows || maxAllows;
  }, [min, max]);

  const clampToBounds = useCallback(
    (next: number): number => {
      let result = next;
      if (min !== undefined) result = Math.max(min, result);
      if (max !== undefined) result = Math.min(max, result);
      return result;
    },
    [min, max]
  );

  // const formatValue = useCallback((nextValue: Value): string => {
  //   return nextValue === null ? '' : String(nextValue);
  // }, []);

  const normalizeRaw = useCallback(
    (raw: string): string => {
      if (!raw) return '';

      if (!isNegativeAllowed) {
        return raw.replace(/[^\d]/g, '');
      }

      const onlyDigitsAndMinus = raw.replace(/[^\d-]/g, '');
      if (!onlyDigitsAndMinus) return '';

      const hasLeadingMinus = onlyDigitsAndMinus.startsWith('-');
      const digitsOnly = onlyDigitsAndMinus.replace(/-/g, '');

      return hasLeadingMinus ? `-${digitsOnly}` : digitsOnly;
    },
    [isNegativeAllowed]
  );

  const handleFocus = useCallback<NonNullable<InputProps['onFocus']>>(
    (event) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    [onFocus]
  );

  const handleChange = useCallback<NonNullable<InputProps['onChange']>>(
    (event) => {
      const raw = event.target.value;
      const normalized = normalizeRaw(raw);

      if (normalized.length === 0) {
        setDisplayValue('');
        onValueChange(null);
        return;
      }

      if (normalized === '-') {
        setDisplayValue('-');
        onValueChange(null);
        return;
      }

      const parsed = Number(normalized);
      if (!Number.isFinite(parsed)) {
        setDisplayValue('');
        onValueChange(null);
        return;
      }

      const clamped = clampToBounds(parsed);
      setDisplayValue(String(clamped));
      onValueChange(clamped);
    },
    [normalizeRaw, clampToBounds, onValueChange]
  );

  useEffect(() => {
    if (value === null) return;

    const clamped = clampToBounds(value);
    if (clamped !== value) {
      onValueChange(clamped);
      if (!isFocused) setDisplayValue(String(clamped));
    }
  }, [value, clampToBounds, onValueChange, isFocused]);

  const formatWithSpaces = (value: number): string => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const formatValue = useCallback((nextValue: Value): string => {
    if (nextValue === null) return '';

    return formatWithSpaces(nextValue);
  }, []);

  const syncDisplayFromPropValue = useCallback(() => {
    setDisplayValue(formatValue(value));
  }, [formatValue, value]);

  useEffect(() => {
    if (isFocused) return;
    syncDisplayFromPropValue();
  }, [isFocused, syncDisplayFromPropValue]);

  const handleBlur = useCallback<NonNullable<InputProps['onBlur']>>(
    (event) => {
      setIsFocused(false);
      syncDisplayFromPropValue();
      onBlur?.(event);
    },
    [onBlur, syncDisplayFromPropValue]
  );

  return (
    <Input
      {...rest}
      inputMode={isNegativeAllowed ? 'text' : 'numeric'}
      value={displayValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  );
};
